import { useEffect, useMemo, useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SunAccent from "@/components/feature/SunAccent";
import Reveal from "@/components/base/Reveal";
import Seo from "@/components/feature/Seo";
import { brand } from "@/mocks/siteData";
import { usePublicFarmers } from "@/hooks/usePublicCatalog";
import FarmersHero from "./components/FarmersHero";
import FarmerFilterBar from "./components/FarmerFilterBar";
import FarmerProfileBlock from "./components/FarmerProfileBlock";
import FarmersCta from "./components/FarmersCta";

export default function Farmers() {
  const [village, setVillage] = useState("all");
  const { data: farmerProfiles, loading, error, reload } = usePublicFarmers();

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const villages = useMemo(
    () => Array.from(new Set(farmerProfiles.map((farmer) => farmer.village))),
    [farmerProfiles],
  );

  const filtered = useMemo(
    () =>
      village === "all"
        ? farmerProfiles
        : farmerProfiles.filter((farmer) => farmer.village === village),
    [village, farmerProfiles],
  );

  return (
    <div className="min-h-screen bg-background-50 overflow-x-hidden">
      <Seo
        title={`Meet Our Farmers | ${brand.name} Agri Tourism`}
        description="Meet the real farm families of Konaseema — their stories, crops and villages. Every host is a working farmer, and every visit supports their family directly."
        path="/farmers"
        keywords="farmers of Konaseema, meet farmers India, farm host families, agri tourism host farmers, farmer stories Andhra Pradesh"
        image="https://readdy.ai/api/search-image?query=Portrait%20of%20a%20friendly%20Indian%20farmer%20standing%20in%20a%20green%20paddy%20field%20at%20sunrise%2C%20warm%20golden%20sunlight%2C%20lush%20green%20tones%2C%20authentic%20documentary%20style%2C%20ultra%20detailed&width=1920&height=1080&seq=farmers-list-cover&orientation=landscape"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Meet Our Farmers",
          description: "Profiles of the farm families who host visitors with Rythugaru Agri Tourism.",
          inLanguage: "en",
        }}
      />
      <Navbar />
      <main>
        <FarmersHero count={farmerProfiles.length} />

        <section className="sun-glaze bg-background-50 py-20 md:py-28">
          <div className="w-full px-4 md:px-8">
            <Reveal className="max-w-3xl">
              <div className="flex items-center gap-3 mb-5">
                <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
                <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
                  Our Goal
                </span>
              </div>
              <h2 className="font-heading text-3xl md:text-5xl leading-tight text-foreground-950 font-semibold">
                More Than Producers — Custodians of the Land
              </h2>
              <p className="mt-6 text-base md:text-lg text-foreground-700 leading-relaxed">
                Our goal is to create additional opportunities for farmers by connecting their
                farms and knowledge with people who want to learn, explore, and experience rural
                life. Every farmer you meet here is a real host, and every visit supports their
                family directly.
              </p>
            </Reveal>
          </div>
        </section>

        <section className="sun-glaze bg-background-100 py-20 md:py-28">
          <div className="w-full px-4 md:px-8">
            {loading && farmerProfiles.length === 0 ? (
              <div className="py-20 flex flex-col items-center gap-4 text-foreground-600">
                <span className="w-12 h-12 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin"></span>
                <p className="text-sm">Loading farmers…</p>
              </div>
            ) : farmerProfiles.length === 0 ? (
              <div className="bg-background-50 rounded-lg border border-background-200 py-16 px-6 text-center">
                <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-signal-wifi-error-line text-3xl"></i>
                </span>
                <h3 className="font-heading text-2xl text-foreground-950 font-semibold mt-5">
                  We couldn't load the farmers
                </h3>
                <p className="mt-3 text-foreground-600 leading-relaxed max-w-md mx-auto">
                  {error || "Please try again in a moment."}
                </p>
                <button
                  type="button"
                  onClick={reload}
                  className="mt-7 inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-7 py-3.5 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line"></i>
                  Try again
                </button>
              </div>
            ) : (
              <>
                <FarmerFilterBar
                  villages={villages}
                  value={village}
                  onChange={setVillage}
                  shown={filtered.length}
                  total={farmerProfiles.length}
                />

                {filtered.length > 0 ? (
                  <div className="space-y-20 md:space-y-28">
                    {filtered.map((farmer, index) => (
                      <FarmerProfileBlock key={farmer.slug} farmer={farmer} index={index} />
                    ))}
                  </div>
                ) : (
                  <div className="bg-background-50 rounded-lg border border-background-200 py-16 px-6 text-center">
                    <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                      <i className="ri-map-pin-line text-3xl"></i>
                    </span>
                    <h3 className="font-heading text-2xl text-foreground-950 font-semibold mt-5">
                      No farmers listed in that village yet
                    </h3>
                    <p className="mt-3 text-foreground-600 leading-relaxed max-w-md mx-auto">
                      We are welcoming new hosts all the time. Meanwhile, explore every farmer we work
                      with across the delta.
                    </p>
                    <button
                      type="button"
                      onClick={() => setVillage("all")}
                      className="mt-7 inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-7 py-3.5 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-refresh-line"></i>
                      Show all farmers
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        <FarmersCta />
      </main>
      <Footer />
    </div>
  );
}